import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { footerData } from '../mock/data';

const publicUrl = process.env.PUBLIC_URL || '';
const footerLogoSrc = `${publicUrl}/transactworld%20logo.png`;

const Footer = () => {
  const paymentPartnerLogos = [
    { name: 'Visa', src: '/images/visa.png' },
    { name: 'MasterCard', src: '/images/MasterCard.png' },
    { name: 'PCI DSS', src: '/images/PCI_DSS-1.png' },
    { name: 'UnionPay', src: '/images/UnionPay.png' },
    { name: 'American Express', src: '/images/AX.png' },
    { name: 'Digicert', src: '/images/Digicert.png' },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(6, 182, 212, 0.3) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Payment Partners */}
      <div className="border-b border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h3
              className="text-2xl font-black text-white mb-2 text-center px-2"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Trusted Payment Partners
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto text-center px-2">
              Secure and reliable payment processing
            </p>
          </motion.div>
          
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-8 sm:gap-x-10 md:gap-x-12">
            {paymentPartnerLogos.map((partner, idx) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08, duration: 0.45 }}
                whileHover={{ scale: 1.05 }}
                className="flex h-14 sm:h-16 md:h-[4.25rem] w-[6.5rem] sm:w-[7.5rem] md:w-[8.5rem] shrink-0 items-center justify-center px-2"
              >
                <img
                  src={partner.src}
                  alt={partner.name}
                  className="max-h-full max-w-full h-auto w-auto object-contain object-center opacity-90 transition-opacity duration-300 hover:opacity-100"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer: mobile = centered stack; md+ = original two-column laptop layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-10">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center text-center w-full max-w-lg md:max-w-md"
          >
            <Link to="/" className="inline-block mb-6">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <img
                  src={footerLogoSrc}
                  alt="TransactWorld Logo"
                  className="h-10 sm:h-12 w-auto mx-auto"
                />
              </motion.div>
            </Link>

            <p className="text-gray-400 leading-relaxed mb-6 text-center hyphens-auto px-1">
              Your trusted global merchant service provider for secure and efficient payment processing.
            </p>

            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                {
                  icon: Facebook,
                  href: 'https://www.facebook.com/grouptransactworld',
                  label: 'Facebook',
                },
                {
                  icon: Twitter,
                  href: 'https://x.com/transactworld_',
                  label: 'X',
                },
                {
                  icon: Linkedin,
                  href: 'https://www.linkedin.com/company/transactworld-limited/posts/?feedView=all',
                  label: 'LinkedIn',
                },
                {
                  icon: Instagram,
                  href: 'https://www.instagram.com/transactworldgroup/',
                  label: 'Instagram',
                },
              ].map((social, idx) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + idx * 0.1 }}
                  whileHover={{
                    scale: 1.2,
                    rotate: 360,
                    boxShadow: '0 10px 20px rgba(59, 130, 246, 0.4)',
                  }}
                  className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20 hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 text-white" />
                </motion.a>
              ))}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2 text-sm text-gray-400 w-full">
              <a href={`mailto:${footerData.email}`} className="hover:text-white transition-colors break-all">
                {footerData.email}
              </a>
              <a href={`tel:${footerData.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                {footerData.phone}
              </a>
              <p className="leading-relaxed">{footerData.address}</p>
            </div>

            {/* Legal: centered two-line layout on mobile; single row on laptop */}
            <nav aria-label="Legal" className="mt-6 w-full md:mt-2">
              <div className="md:hidden flex flex-col items-center gap-3 text-xs sm:text-sm px-2">
                <div className="flex flex-nowrap justify-center items-center gap-x-2 sm:gap-x-4">
                  <Link
                    to="/cookie-policy"
                    className="text-gray-300 hover:text-violet-300 transition-colors whitespace-nowrap shrink-0"
                  >
                    Cookie Policy
                  </Link>
                  <span className="text-gray-600 select-none shrink-0" aria-hidden>
                    |
                  </span>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-300 hover:text-violet-300 transition-colors whitespace-nowrap shrink-0"
                  >
                    Privacy Policy
                  </Link>
                </div>
                <Link
                  to="/terms"
                  className="text-gray-300 hover:text-violet-300 transition-colors text-center leading-snug"
                >
                  General Terms and Conditions
                </Link>
              </div>

              <div className="hidden md:flex flex-wrap gap-x-6 gap-y-2 justify-center">
                {[
                  { name: 'Cookie Policy', href: '/cookie-policy' },
                  { name: 'Privacy Policy', href: '/privacy-policy' },
                  { name: 'General Terms and Conditions', href: '/terms' },
                ].map((link, idx) => (
                  <Link key={link.name} to={link.href}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + idx * 0.1 }}
                      whileHover={{
                        x: 6,
                        color: '#a78bfa',
                        textShadow: '0 0 20px rgba(167, 139, 250, 0.5)',
                      }}
                      className="text-sm text-gray-400 transition-all cursor-pointer"
                    >
                      {link.name}
                    </motion.div>
                  </Link>
                ))}
              </div>
            </nav>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-3 border-t border-white/10 pt-8 md:border-t-0 md:pt-0 w-full max-w-2xl md:max-w-none"
          >
            <p className="text-sm text-gray-400 text-center">
              {footerData.copyright} All rights reserved.
            </p>
            <p className="text-xs text-white leading-relaxed text-justify hyphens-auto">
              TransactWorld Limited. 3 Thivon Street, Anna Court 8-9, Office 202, CY-1056 Nicosia, Cyprus I
              support@transactworld.com I Contact +44 20 3129 7109
            </p>

            <p className="text-xs text-white leading-relaxed text-justify hyphens-auto">
              Transactworld Limited (acting under trading name &quot;Transactworld&quot;) is an authorized EMI
              agent working under Payswix, UAB (acting under trading name &quot;payswix&quot;) license (Nr. 21)
              and supervised by Central Bank of Lithuania solely for the provision of IBAN Solutions. All other
              services, are offered independently by Transactworld.
            </p>

            <p className="text-xs text-white leading-relaxed text-justify hyphens-auto">
              For IBAN Solutions service, Transactworld Ltd acts as a data processor on behalf of Payswix UAB, an
              Electronic Money Institution (EMI) licensed and supervised by the Bank of Lithuania, which acts as
              a data collector (hereinafter - payswix). Payswix is the service provider and the data controller
              responsible for the processing of personal data collected via this website providing IBAN Solutions
              services. The full privacy policy, including information about your rights and how your data is
              handled by payswix, is available on the official payswix website.
            </p>
          </motion.div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
